import React, { Component } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import i18n from 'i18n-react';
import { capitalize } from 'lodash';

const defaultTransitions = {
  width: '200ms',
  height: '200ms',
  borderRadius: '1000ms',
};

export default class PointsDisplay extends Component {
  constructor(props) {
    super(props);

    const { points, tiers, labels } = this.props;
    const ranks = {};
    tiers.forEach((tier, i) => {
      ranks[tier] = labels[i];
    });

    this.state = {
      progress: null,
      points,
      ranks,
    };
  }

  componentDidMount() {
    this.calculateProgress();
  }

  componentDidUpdate(prevProps, prevState) {
    this.recalculateValues(prevProps, prevState);
  }

  getTieredValue(array = null, pointsOverride = null) {
    const { tiers, points: pointsProp } = this.props;
    let index = 0;
    tiers.forEach((points, i) => {
      // Could be improved, maybe using filter / reduce?
      if ((pointsOverride || pointsProp) > points && index !== i) {
        index = i;
      }
    });

    return array ? array[index < 0 ? 0 : index] : index;
  }

  getStyleObject() {
    const { tierColors, textColors } = this.props;
    const style = {};

    const backgroundColor = tierColors.length > 0 && this.getTieredValue(tierColors);
    const color = textColors.length > 0 && this.getTieredValue(textColors);

    if (backgroundColor) style.backgroundColor = backgroundColor;
    if (color) style.color = color;

    return style;
  }

  recalculateValues(prevProps, prevState) {
    const { points } = this.props;
    const { points: pointState } = this.state;
    const { points: prevPointsProp } = prevProps;
    const { points: prevPointsState } = prevState;

    const changed = prevPointsProp !== points || prevPointsState !== points;
    if (changed && pointState !== points) {
      this.setState({ points }, () => {
        this.calculateProgress(points);
      });
    }
  }

  calculateProgress(newPoints = 0) {
    const { points: oldPoints, tiers, segmented } = this.props;
    const { progress } = this.state;

    const points = newPoints || oldPoints;
    const tierIndex = this.getTieredValue() + 1 < tiers.length ? this.getTieredValue() + 1 : tiers.length - 1;

    const prevTier = tiers[Math.max(tierIndex - 1, 0)];
    const newProgress = Math.round(((points - prevTier) / (tiers[tierIndex] - prevTier)) * 100);
    const currentTierPoints = tiers[this.getTieredValue()];

    const tier = Math.min(this.getTieredValue() + 1, tiers.length);
    const segmentedProgress =
      (100 / tiers.length) * tier +
      ((points - currentTierPoints) / (tiers[tiers.length - 1] - currentTierPoints)) * 100;
    const progressOutdated = progress !== newProgress && progress !== 100;

    if (!Number.isNaN(newProgress) && progressOutdated) {
      this.setState({
        progress: Math.min(segmented ? segmentedProgress : newProgress, 100),
      });
    }
  }

  render() {
    const { tiers, segmented, orientation, transitions, onClick, startingValue, minSize } = this.props;
    const { progress, points: pointState, ranks } = this.state;

    const currentTier = tiers && this.getTieredValue();
    const nextTier = tiers && (currentTier + 1 < tiers.length ? currentTier + 1 : tiers.length - 1);

    const mergedTransitions = { ...defaultTransitions, ...transitions };

    const transition = {
      transition: Object.keys(mergedTransitions)
        .map((key) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()} ${mergedTransitions[key]}`)
        .join(', '),
    };

    const key = orientation === 'horizontal' ? 'width' : 'height';
    const wrapper = document.querySelector('.wrapper__bar');
    const value = +wrapper && wrapper[`client${capitalize(key)}`] / tiers.length;

    const overlayStyle = {
      [key]: `${
        progress
          ? Math.floor(progress)
          : Math.floor((startingValue / (segmented ? tiers[tiers.length - 1] : 1)) * (segmented ? 100 : 1))
      }%`,
    };

    const wrapperStyle = minSize ? { [`min${capitalize(key)}`]: minSize } : {};
    const points = pointState >= tiers[tiers.length - 1] ? tiers[tiers.length - 1] : pointState;
    const styleObject = this.getStyleObject();

    return (
      <button
        type="button"
        className={clsx('wrapper__bar', {
          horizontal: orientation === 'horizontal',
          vertical: orientation === 'vertical',
        })}
        style={{ ...wrapperStyle, ...transition }}
        onClick={onClick}
      >
        {segmented &&
          tiers.map((tier, i) => (
            <div
              key={tier}
              className={clsx('progress__bar', orientation, {
                start: i === 0,
                end: i === tiers.length - 1,
              })}
              style={styleObject}
            >
              <span>{ranks[tier]}</span>
            </div>
          ))}
        {!segmented && (
          <div className="progress__bar full" style={styleObject}>
            <span className={clsx('progress__info', orientation)}>
              <span>{i18n.translate('points.outOf', { points })}</span>
              <span>{`${tiers[nextTier]} ${i18n.translate('points.unit', { points })}`}</span>
            </span>
          </div>
        )}
        <div
          className={clsx('progress__overlay', orientation, { end: Math.floor(progress) >= 95 })}
          style={{ ...overlayStyle, ...transition }}
        >
          {segmented && tiers.map((tier) => <span key={tier} style={{ flex: `0 0 ${value}px` }} />)}
          {!segmented && (
            <span
              className={clsx('progress__info full', orientation)}
              style={{ flex: `0 0 ${value * tiers.length}px` }}
            />
          )}
        </div>
      </button>
    );
  }
}

PointsDisplay.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string),
  minSize: PropTypes.string,
  onClick: PropTypes.func,
  orientation: PropTypes.string,
  points: PropTypes.number,
  segmented: PropTypes.bool,
  startingValue: PropTypes.number,
  textColors: PropTypes.arrayOf(PropTypes.string),
  tierColors: PropTypes.arrayOf(PropTypes.string),
  tiers: PropTypes.arrayOf(PropTypes.number),
  transitions: PropTypes.shape(),
};

PointsDisplay.defaultProps = {
  labels: [],
  minSize: null,
  onClick: null,
  orientation: 'horizontal',
  points: 0,
  segmented: false,
  startingValue: 0,
  textColors: [],
  tierColors: [],
  tiers: [0],
  transitions: defaultTransitions,
};
